import { Ticket, TicketType } from '../types/ticket';
import { ParkingLocation } from '../types/parkingLocation';
import { getLookupStartDate } from './getLookupStartDate';

const HOURLY_TOW_LOOKBACK_DAYS = 30;
const HOURLY_SESSION_MAX_MINUTES = 12 * 60;

export type ViolationDecision =
	| { action: 'suggestTicket'; ticketType: TicketType }
	| { action: 'withinLimit'; minutesRemaining: number }
	| { action: 'sessionClosed' };

/**
 * Determines the appropriate response for the next scan of a plate at a parking
 * location, based on the location's violation-check mode and the plate's past
 * tickets.
 *
 * - `fixedMonthly` / `rolling`: counts past non-void tickets in the window and
 *   escalates via warning/tow policies.
 * - `hourlyExpiry`: treats the most recent ticket as the start of a parking
 *   session and flags an overstay only after `maxStayMinutes` elapses.
 */
export function getNextViolationTicketType({
	tickets,
	parkingLocation,
}: {
	tickets: Ticket[];
	parkingLocation: ParkingLocation;
}): ViolationDecision {
	if (parkingLocation.violationCheckType === 'hourlyExpiry') {
		return decideHourly({ tickets, parkingLocation });
	}

	return decideCounting({ tickets, parkingLocation });
}

function decideCounting({
	tickets,
	parkingLocation,
}: {
	tickets: Ticket[];
	parkingLocation: ParkingLocation;
}): ViolationDecision {
	const lookupPeriod =
		parkingLocation.violationCheckType === 'fixedMonthly'
			? -1
			: parkingLocation.violationCheckWindowDays || 0;

	const lookupStartDate = new Date(getLookupStartDate(lookupPeriod));

	const relevantTickets = tickets.filter((ticket) => {
		const ticketDate = new Date(ticket.date);
		return !ticket.isVoid && ticketDate >= lookupStartDate;
	});

	const violationCount = relevantTickets.length;

	if (
		parkingLocation.towPolicyType === 'afterXViolations' &&
		typeof parkingLocation.towAfterViolations === 'number'
	) {
		if (violationCount >= parkingLocation.towAfterViolations) {
			return { action: 'suggestTicket', ticketType: 'tow' };
		}
	}

	if (
		parkingLocation.taggingPolicyType === 'afterXViolations' &&
		typeof parkingLocation.taggingAfterViolations === 'number' &&
		parkingLocation.taggingAfterViolations > 0
	) {
		const taggingInterval = parkingLocation.taggingAfterViolations;
		const nextViolationIndex = violationCount + 1;
		const ticketType: TicketType =
			nextViolationIndex % taggingInterval === 0 ? 'logAndWarn' : 'log';
		return { action: 'suggestTicket', ticketType };
	}

	return { action: 'suggestTicket', ticketType: 'log' };
}

function decideHourly({
	tickets,
	parkingLocation,
}: {
	tickets: Ticket[];
	parkingLocation: ParkingLocation;
}): ViolationDecision {
	const limitMinutes = parkingLocation.maxStayMinutes || 0;

	// Safety fallback: a misconfigured hourly lot should not silently log as a violation.
	// Treat as a plain arrival log.
	if (limitMinutes <= 0) {
		return { action: 'suggestTicket', ticketType: 'log' };
	}

	const nonVoidTickets = tickets
		.filter((t) => !t.isVoid)
		.slice()
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

	const lastTicket = nonVoidTickets[0];

	if (!lastTicket) {
		return { action: 'suggestTicket', ticketType: 'log' };
	}

	const elapsedMinutes =
		(Date.now() - new Date(lastTicket.date).getTime()) / 60000;

	// Session-age cap: if the last ticket is older than 12h, the car has
	// effectively ended its session and a new scan is a fresh arrival.
	if (elapsedMinutes > HOURLY_SESSION_MAX_MINUTES) {
		return { action: 'suggestTicket', ticketType: 'log' };
	}

	if (lastTicket.ticketType === 'log') {
		if (elapsedMinutes <= limitMinutes) {
			return {
				action: 'withinLimit',
				minutesRemaining: Math.max(
					0,
					Math.round(limitMinutes - elapsedMinutes),
				),
			};
		}
		// Overstay
		return { action: 'suggestTicket', ticketType: hourlyOverstayType(tickets, parkingLocation) };
	}

	// lastTicket is logAndWarn or tow — session was already closed
	if (elapsedMinutes <= limitMinutes) {
		return { action: 'sessionClosed' };
	}
	// Enough time has passed that we treat this as a new arrival
	return { action: 'suggestTicket', ticketType: 'log' };
}

function hourlyOverstayType(
	tickets: Ticket[],
	parkingLocation: ParkingLocation,
): TicketType {
	if (
		parkingLocation.towPolicyType !== 'afterXViolations' ||
		typeof parkingLocation.towAfterViolations !== 'number'
	) {
		return 'logAndWarn';
	}

	const lookbackStart = Date.now() - HOURLY_TOW_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
	const priorOverstayCount = tickets.filter((t) => {
		if (t.isVoid) return false;
		if (t.ticketType !== 'logAndWarn' && t.ticketType !== 'tow') return false;
		return new Date(t.date).getTime() >= lookbackStart;
	}).length;

	return priorOverstayCount >= parkingLocation.towAfterViolations
		? 'tow'
		: 'logAndWarn';
}
